import type { DelayDeliveryTryRow } from '@/types';

/**
 * The delivery state of a retry row.
 *
 * ⚠️ `delivered` and `archived` are separate columns and the combination
 * matters: production rows exist with `delivered = 0` AND a `delivered_at`
 * timestamp, which reads as success but is not one — the retry loop stopped.
 * So state is derived from `delivered`/`archived` only, and `delivered_at` is
 * never treated as evidence of delivery.
 */
export type DeliveryState = 'delivered' | 'archived' | 'pending';

export function deliveryState(row: DelayDeliveryTryRow): DeliveryState {
  if (row.archived) return 'archived';
  return row.delivered ? 'delivered' : 'pending';
}

/** The message we are trying to deliver. Shape is the producer's, so read it defensively. */
export interface DeliveryPayload {
  betId?: string;
  roundId?: string;
  gameUuid?: string;
  playerId?: string;
  sessionId?: string;
  amount?: string;
  currency?: string;
  type?: string;
  client?: string;
  freeroundId?: string;
}

export function payloadOf(row: DelayDeliveryTryRow): DeliveryPayload {
  const p = row.payload;
  return p && typeof p === 'object' ? (p as DeliveryPayload) : {};
}

/**
 * The operator's response to the last attempt. It is stored as a JSON *string*
 * (unlike `payload`, which is a json column), and a non-JSON value is perfectly
 * possible — a transport error, a truncated body. Never throws: an unparseable
 * value is returned as its raw text, which is still the most useful thing to show.
 */
export function lastResult(row: DelayDeliveryTryRow): {
  code: string | null;
  message: string | null;
  raw: string | null;
  parsed: unknown;
} {
  const raw = row.last_try_result;
  if (!raw) return { code: null, message: null, raw: null, parsed: null };

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const code = typeof parsed?.code === 'string' ? parsed.code : null;
    const message = typeof parsed?.message === 'string' ? parsed.message : null;
    return { code, message, raw, parsed };
  } catch {
    return { code: null, message: null, raw, parsed: null };
  }
}
